import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Grid,
  Image,
  Loader,
  FileInput,
  NumberInput,
  SimpleGrid,
  Card,
  ActionIcon,
  Text,
  Modal,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import axios from 'axios';
import { useAppSelector } from '../../../../store/hooks';
import { IconTrash, IconEdit, IconUpload } from '@tabler/icons-react';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface ProcessStep {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  order: number;
  active: boolean;
}

export const QualityProcessGalleryPage = () => {
  const { token } = useAppSelector((state) => state.auth);
  const [steps, setSteps] = useState<ProcessStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProcessStep | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      order: 0,
      active: true,
    },
  });

  const fetchSteps = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery/QUALITY_PROCESS`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const gallery = res.data;
      const items = gallery.items
        .filter((item: any) => item.type === 'IMAGE')
        .map((item: any) => {
          let meta = {};
          try { meta = JSON.parse(item.metadata || '{}'); } catch(e) {}
          return {
            id: item.id,
            title: item.title,
            description: item.description,
            imageUrl: item.imageUrl,
            order: (meta as any).order || 0,
            active: item.active,
          };
        })
        .sort((a: ProcessStep, b: ProcessStep) => a.order - b.order);
      setSteps(items);
    } catch (error) {
      console.error('Failed to fetch quality process steps:', error);
      notifications.show({ title: 'Error', message: 'Failed to load steps', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteps();
  }, []);

  const resetForm = () => {
    form.reset();
    setImageFile(null);
    setEditingId(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!imageFile && !editingId) {
      notifications.show({ title: 'Error', message: 'Please select an image', color: 'red' });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    const metadata = { order: values.order };
    const data = {
      title: values.title,
      description: values.description,
      type: 'IMAGE',
      active: values.active,
      orderIndex: 0,
      metadata: JSON.stringify(metadata),
    };
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob);
    if (imageFile) formData.append('file', imageFile);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/gallery/items/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Step updated', color: 'green' });
      } else {
        await axios.post(`${API_URL}/gallery/QUALITY_PROCESS/items`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Step added', color: 'green' });
      }
      resetForm();
      fetchSteps();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Operation failed', color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/gallery/items/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: 'Success', message: 'Step deleted', color: 'green' });
      closeDeleteModal();
      fetchSteps();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Delete failed', color: 'red' });
    } finally {
      setSubmitting(false);
      setDeleteTarget(null);
    }
  };

  const editStep = (step: ProcessStep) => {
    setEditingId(step.id);
    form.setValues({
      title: step.title,
      description: step.description,
      order: step.order,
      active: true,
    });
    setImageFile(null);
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Title order={2} mb="lg">Manage Quality Process Steps</Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">{editingId ? 'Edit Step' : 'Add New Step'}</Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Step Title *"
                  placeholder="e.g., Raw Material Selection"
                  {...form.getInputProps('title')}
                  required
                />
                <Textarea
                  label="Description"
                  placeholder="Brief description"
                  {...form.getInputProps('description')}
                />
                <NumberInput
                  label="Display Order"
                  placeholder="0, 1, 2..."
                  {...form.getInputProps('order')}
                />
                <FileInput
                  label="Step Image"
                  placeholder="Click to upload"
                  accept="image/png,image/jpeg,image/svg+xml"
                  value={imageFile}
                  onChange={setImageFile}
                  leftSection={<IconUpload size={16} />}
                />
                <Group justify="flex-end" mt="md">
                  {editingId && (
                    <Button variant="light" onClick={cancelEdit}>Cancel</Button>
                  )}
                  <Button type="submit" color="primary" loading={submitting}>
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">Existing Steps ({steps.length})</Title>
            {steps.length === 0 ? (
              <Text c="dimmed">No steps added yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {steps.map((step) => (
                  <Card key={step.id} withBorder padding="sm">
                    <Card.Section>
                      <Image
                        src={step.imageUrl.startsWith('http') ? step.imageUrl : `${API_URL}${step.imageUrl}`}
                        height={100}
                        fit="cover"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200' viewBox='0 0 400 200'%3E%3Crect width='400' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E"
                      loading="lazy" 
                      />
                    </Card.Section>
                    <Stack mt="xs" gap={5}>
                      <Text fw={600}>{step.title}</Text>
                      <Text size="xs" c="dimmed">{step.description}</Text>
                      <Text size="xs">Order: {step.order}</Text>
                      <Group justify="space-between" mt="xs">
                        <ActionIcon color="blue" onClick={() => editStep(step)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          onClick={() => {
                            setDeleteTarget(step);
                            openDeleteModal();
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Confirm Deletion">
        <Text>
          Are you sure you want to delete the step <strong>{deleteTarget?.title}</strong>? This action cannot be undone.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={submitting}>Delete</Button>
        </Group>
      </Modal>
    </Container>
  );
};